import {
  CAPACITY_PER_PRODUCTION_BUILDING,
  CAPTURE_FULL,
  INCOME_PER_PROPERTY,
  isCannonCodexEnabled,
  MAX_HP,
  REPAIR_HP_RATE,
} from "@dailywars/core/engine";
import type { ReactNode } from "react";

import { SectionHeader } from "#/components/SectionHeader";

const REPAIR_PERCENT = Math.round(REPAIR_HP_RATE * 100);
const HP_DISPLAY_MAX = MAX_HP / 10;

type GuideItem = { term: string; body: ReactNode };
type GuideSection = { title: string; items: GuideItem[] };

const HOW_TO_WIN_BASE_ITEMS: GuideItem[] = [
  {
    term: "Objective:",
    body: "Each map has one objective that varies between maps. The different objectives are:",
  },
  {
    term: "Rout:",
    body: "You win by destroying all enemy units.",
  },
  {
    term: "Capture:",
    body: "You win by capturing X cities.",
  },
  {
    term: "Monuments:",
    body: "You win by capturing all monuments.",
  },
  {
    term: "Escort:",
    body: "You win by escorting every Supply Truck to a Drop Zone tile. Losing a single Supply Truck fails the mission.",
  },
];

const CANNON_HOW_TO_WIN_ITEM: GuideItem = {
  term: "Cannons:",
  body: "You win by destroying all enemy cannons.",
};

const OTHER_GUIDE_SECTIONS: GuideSection[] = [
  {
    title: "Buildings & capturing",
    items: [
      {
        term: "Capture buildings:",
        body: "Only infantry (Rifleman, RPG, and Sniper) can capture neutral and enemy buildings.",
      },
      {
        term: "Capture progress:",
        body: `Capturing requires ${CAPTURE_FULL} capture points. Each capture action captures points equal to the unit's displayed HP, so a full-health unit (${HP_DISPLAY_MAX} HP) finishes in two turns. If the capturer moves off the building or is destroyed, progress resets.`,
      },
      {
        term: "Income:",
        body: `Most buildings pay ${INCOME_PER_PROPERTY} funds at the start of your turn.`,
      },

      {
        term: "Production:",
        body: "Each production building can build one unit per turn. New units can't move or act until your next turn.",
      },
      {
        term: "Capacity:",
        body: `Each owned barracks, factory, base, or airport adds ${CAPACITY_PER_PRODUCTION_BUILDING} to that building's unit limits (foot, vehicle, or air). Bases add both foot and vehicle capacity. You can't build a unit if you're at the cap.`,
      },
      {
        term: "Repairs:",
        body: `Damaged units on your buildings heal ${REPAIR_PERCENT}% max HP for ${REPAIR_PERCENT}% of their build cost (skipped if you can't afford it). Foot units repair on cities, barracks, and bases; vehicles on cities, factories, and bases; air units on airports.`,
      },
    ],
  },
  {
    title: "Combat",
    items: [
      {
        term: "Displayed HP:",
        body: `Units show 1–${HP_DISPLAY_MAX} HP. Full health is ${HP_DISPLAY_MAX}. Finer damage is tracked internally, but combat and capturing always use the number on screen. A unit showing 7 HP hits at 70% strength.`,
      },
      {
        term: "Terrain defense:",
        body: "Terrain reduces incoming damage by the tile's defense %. Air units ignore terrain defense.",
      },
      {
        term: "Direct combat:",
        body: "Direct-fire units (range 1) attack adjacent enemies. Direct units counterattack when attacked (if they survive the initial hit).",
      },

      {
        term: "Indirect combat:",
        body: "Indirect-fire units (range 2+) attack from a safe distance. They do not trigger counterattacks, and cannot counterattack themselves. They generally cannot move and shoot in the same turn, except for Sniper.",
      },
      {
        term: "Monuments:",
        body: "Each monument you own increases your units' firepower by 10%.",
      },
      {
        term: "Combine units:",
        body: "Move a unit onto an identical ally when at least one is below full HP to merge their health. Combined HP is capped at 100%; extra HP is refunded as funds.",
      },
    ],
  },
];

function howToWinItems(atMs = Date.now()): GuideItem[] {
  if (!isCannonCodexEnabled(atMs)) return HOW_TO_WIN_BASE_ITEMS;
  return [...HOW_TO_WIN_BASE_ITEMS, CANNON_HOW_TO_WIN_ITEM];
}

function guideSections(atMs = Date.now()): GuideSection[] {
  return [{ title: "How to win", items: howToWinItems(atMs) }, ...OTHER_GUIDE_SECTIONS];
}

function CodexGuideSection({ section }: { section: GuideSection }) {
  return (
    <section className="flex flex-col gap-2">
      <SectionHeader>{section.title}</SectionHeader>
      <ul className="list-disc space-y-2 pl-5 text-sm leading-6 text-muted-foreground">
        {section.items.map((item) => (
          <li key={item.term}>
            <span className="font-semibold text-foreground">{item.term}</span> {item.body}
          </li>
        ))}
      </ul>
    </section>
  );
}

export function CodexGuideSections() {
  return (
    <>
      {guideSections().map((section) => (
        <CodexGuideSection key={section.title} section={section} />
      ))}
    </>
  );
}
